import Avatar from "@/components/base/Avatar";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import StatusBadge from "@/components/base/StatusBadge";
import { formatDate } from "@/utils/format";
import { roleTone } from "@/utils/staff";
import type { StaffMember } from "@/types/portal";

interface UserTableProps {
  rows: StaffMember[];
  canManage: boolean;
  currentUserId: string;
  onToggleStatus: (member: StaffMember) => void;
  onResend: (member: StaffMember) => void;
  onChangeRole: (member: StaffMember) => void;
}

export default function UserTable({
  rows,
  canManage,
  currentUserId,
  onToggleStatus,
  onResend,
  onChangeRole,
}: UserTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-team-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No users match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another status tab, or clear the role, market and search filters.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[900px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">Member</th>
            <th className="px-3 py-3 font-semibold">Role</th>
            <th className="px-3 py-3 font-semibold">Markets</th>
            <th className="px-3 py-3 font-semibold">Status</th>
            <th className="px-3 py-3 font-semibold">Last active</th>
            <th className="px-5 py-3 text-right font-semibold">Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((member) => {
            const isSelf = member.id === currentUserId;
            return (
              <tr
                key={member.id}
                className="border-b border-background-100 last:border-0 transition-colors hover:bg-background-100"
              >
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar src={member.avatar} alt={member.name} size="sm" />
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm text-foreground-900">
                        {member.name}
                        {isSelf ? (
                          <span className="rounded-full bg-background-200 px-1.5 text-[10px] text-foreground-600">
                            You
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-foreground-500">
                        {member.email}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <Badge tone={roleTone(member.roleId)}>
                    {member.roleName}
                  </Badge>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex max-w-[220px] flex-wrap gap-1">
                    {member.markets.slice(0, 2).map((market) => (
                      <span
                        key={market}
                        className="rounded-full border border-background-300 px-2 py-0.5 text-[11px] text-foreground-600"
                      >
                        {market}
                      </span>
                    ))}
                    {member.markets.length > 2 ? (
                      <span className="rounded-full border border-background-300 px-2 py-0.5 text-[11px] text-foreground-500">
                        +{member.markets.length - 2}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex flex-col items-start gap-1">
                    <StatusBadge status={member.status} />
                    {member.status === "Invited" && member.invitedBy ? (
                      <span className="text-[11px] text-foreground-400">
                        by {member.invitedBy}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm text-foreground-700">
                      {member.status === "Invited"
                        ? `Invited ${formatDate(member.invitedAt ?? member.lastActive)}`
                        : formatDate(member.lastActive)}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 text-[11px] ${
                        member.twoFactor
                          ? "text-accent-700"
                          : "text-foreground-400"
                      }`}
                    >
                      <i
                        className={
                          member.twoFactor
                            ? "ri-shield-check-line"
                            : "ri-shield-line"
                        }
                      />
                      {member.twoFactor ? "2FA on" : "2FA off"}
                    </span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <div className="flex items-center justify-end gap-1.5">
                    {canManage && !isSelf ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="ri-shield-user-line"
                        onClick={() => onChangeRole(member)}
                      >
                        Change role
                      </Button>
                    ) : null}
                    {member.status === "Invited" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        icon="ri-mail-send-line"
                        disabled={!canManage}
                        onClick={() => onResend(member)}
                      >
                        Resend
                      </Button>
                    ) : member.status === "Active" ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon="ri-user-unfollow-line"
                        disabled={!canManage || isSelf}
                        title={
                          isSelf
                            ? "You cannot deactivate your own account"
                            : undefined
                        }
                        onClick={() => onToggleStatus(member)}
                      >
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        icon="ri-user-follow-line"
                        disabled={!canManage}
                        onClick={() => onToggleStatus(member)}
                      >
                        Reactivate
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
